import {hubspotAPI} from '@commercial-website/consent'

import {isBrowser} from './browser'

/**
 * Obfuscates data with Base64 encoding.
 * This provides basic obfuscation to hide data from casual inspection,
 * but is NOT cryptographically secure.
 */
export const obfuscateData = (data: string): string => {
    return btoa(encodeURIComponent(data))
}

/**
 * Deobfuscates data that was obfuscated using obfuscateData.
 */
export const deobfuscateData = (obfuscatedData: string): string => {
    return decodeURIComponent(atob(obfuscatedData))
}

/**
 * Increment version in case of change of obfuscation logic,
 * incrementing will effectively clear stale data on clients device
 */
const STORAGE_VERSION = '2'

const createStorageApi = (storage: Storage | undefined) => ({
    /**
     * Retrieves and unobfuscates value from storage. Returns 'null' if
     * no value is found for that key
     */
    getItem: (key: string) => {
        if (!isBrowser || !storage) {
            return null
        }
        const item = storage.getItem(`${key}/v${STORAGE_VERSION}`)
        if (!item) {
            return null
        }
        try {
            return deobfuscateData(item)
        } catch (error) {
            console.warn(`Could not get item ${key}:`, error)
            return null
        }
    },
    /**
     * Sets and obfuscates value to storage which prevents casual inspection of
     * stored data
     */
    setItem: (key: string, value: string, consentCategory: keyof Hubspot.ConsentCategories) => {
        if (!isBrowser || !storage) {
            return
        }
        if (!hubspotAPI.hasConsent(consentCategory)) {
            console.warn(`Cannot save to storage "${key}": missing consent for ${consentCategory}`)
            return
        }
        try {
            const obfuscatedData = obfuscateData(value)
            storage.setItem(`${key}/v${STORAGE_VERSION}`, obfuscatedData)
        } catch (error) {
            console.warn(`Could not set item ${key}:`, error)
            return
        }
    },
})

export const sessionStorage = createStorageApi(isBrowser ? window.sessionStorage : undefined)
export const localStorage = createStorageApi(isBrowser ? window.localStorage : undefined)
